import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Filter, ChevronDown, ChevronRight, Repeat } from 'lucide-react';
import {
  STATUS_LABEL, STATUS_TONE, TONE_STYLE,
  displayFulfillmentType,
} from '@/lib/usbUxPresentation';
import ConvertDemoModal from './ConvertDemoModal';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusChip = ({ status }) => {
  const tone = STATUS_TONE[status] || 'neutral';
  const s = TONE_STYLE[tone];
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 3,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{STATUS_LABEL[status] || status}</span>
  );
};

const TypeChip = ({ order }) => {
  const t = displayFulfillmentType(order);
  const style = (() => {
    if (t === 'Demo')             return { bg: '#FEF3C7', fg: '#92400E', border: '#FDE68A' };
    if (t === 'Replacement')      return { bg: '#EDE9FE', fg: '#5B21B6', border: '#C4B5FD' };
    if (t === 'Internal / Test')  return { bg: '#E0F2FE', fg: '#075985', border: '#7DD3FC' };
    if (t === 'Legacy Customer')  return { bg: '#F3F4F6', fg: '#4B5563', border: '#D1D5DB' };
    return { bg: '#DBEAFE', fg: '#1E40AF', border: '#93C5FD' };
  })();
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 3,
      background: style.bg, color: style.fg, border: `1px solid ${style.border}`,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{t}</span>
  );
};

const AdvancedAuditDetails = ({ order }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
      <button onClick={() => setOpen((o) => !o)}
              data-testid={`detail-advanced-toggle-${order.order_id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', color: '#4B5563',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Advanced / Audit Details
      </button>
      {open && (
        <pre data-testid={`detail-advanced-${order.order_id}`}
             style={{
               marginTop: 10, background: '#F9FAFB', padding: 10,
               fontSize: 11, color: '#111827', border: '1px solid #E5E7EB',
               maxHeight: 420, overflow: 'auto',
             }}>
          {JSON.stringify(order, null, 2)}
        </pre>
      )}
    </div>
  );
};

const Row = ({ order, onOpen, isOpen, onConvertClick }) => {
  const type = displayFulfillmentType(order);
  const date = (order.created_at || '').slice(0, 10);
  const price = (() => {
    if (order.fulfillment_type === 'demo') return 'FREE DEMO';
    const t = (order.line_items || []).reduce((a, li) => a + (li.unit_price || 0) * (li.quantity || 1), 0);
    return t ? `$${t.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';
  })();
  const serials = (order.drives || []).map((d) => d.serial).filter(Boolean);
  const rep = order?.demo_metadata?.sales_rep || order?.sales_rep || '—';

  return (
    <>
      <tr
        data-testid={`history-row-${order.order_id}`}
        onClick={() => onOpen(order.order_id)}
        style={{ cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
      >
        <td style={{ padding: '10px 8px', fontSize: 12 }}>{date}</td>
        <td style={{ padding: '10px 8px', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
          {serials.length ? serials[0] : order.order_id}
        </td>
        <td style={{ padding: '10px 8px' }}><TypeChip order={order} /></td>
        <td style={{ padding: '10px 8px', fontSize: 13, fontWeight: 600, color: '#0F1B2D' }}>
          {order.school_name || order.district_name || '—'}
        </td>
        <td style={{ padding: '10px 8px', fontSize: 12 }}>{order.initial_grade_band || '—'}</td>
        <td style={{ padding: '10px 8px' }}><StatusChip status={order.status} /></td>
        <td style={{ padding: '10px 8px', fontSize: 12 }}>{rep}</td>
        <td style={{ padding: '10px 8px', fontSize: 12, textAlign: 'right' }}>{price}</td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={8} style={{ background: '#F9FAFB', padding: 20 }}>
            <div data-testid={`history-detail-${order.order_id}`}>
              <h3 style={{ fontSize: 16, marginTop: 0, color: '#0F1B2D' }}>
                {serials[0] || order.order_id}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                <div><strong>Fulfillment Type:</strong> {type}</div>
                <div><strong>Status:</strong> {STATUS_LABEL[order.status] || order.status}</div>
                <div><strong>School / Recipient:</strong> {order.school_name || '—'}</div>
                <div><strong>District / Prospect:</strong> {order.district_name || '—'}</div>
                <div><strong>Buyer contact:</strong> {order?.buyer_contact?.name || '—'} · {order?.buyer_contact?.email || ''}</div>
                <div><strong>Grade Band:</strong> {order.initial_grade_band || '—'}</div>
                <div><strong>Platform:</strong> {order.platform_profile || 'macos'}</div>
                <div><strong>Price:</strong> {price}</div>
                <div><strong>Quote:</strong> {order.quote_number || '—'}</div>
                <div><strong>PO:</strong> {order.po_number || '—'}</div>
                <div><strong>Sales Rep:</strong> {rep}</div>
                <div><strong>Shipping:</strong> {order?.shipping_address?.line1 ? `${order.shipping_address.line1}, ${order.shipping_address.city}, ${order.shipping_address.state} ${order.shipping_address.zip}` : '—'}</div>
                <div><strong>Tracking:</strong> {order.tracking_number || '—'} ({order.carrier || '—'})</div>
                <div><strong>Delivered:</strong> {order.shipped_at ? order.shipped_at.slice(0, 10) : '—'}</div>
                {order.fulfillment_type === 'demo' && (
                  <>
                    <div><strong>Demo duration:</strong> {order?.demo_metadata?.duration_days || '—'} days</div>
                    <div><strong>Conversion status:</strong> {order?.demo_metadata?.conversion_status || '—'}</div>
                  </>
                )}
                {order.replaces_serial && <div><strong>Replaces serial:</strong> {order.replaces_serial}</div>}
                {order.replaced_by_serial && <div><strong>Replaced by serial:</strong> {order.replaced_by_serial}</div>}
                {order.converted_from_demo_id && (
                  <div data-testid={`detail-converted-from-${order.order_id}`}>
                    <strong>Converted from demo:</strong>{' '}
                    <code style={{ fontSize: 12 }}>{order.converted_from_demo_id}</code>
                  </div>
                )}
              </div>

              {/* UX1C · Demo → Paid conversion action */}
              {order.fulfillment_type === 'demo' && (
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #E5E7EB' }}>
                  {order?.demo_metadata?.converted_to_order_id ? (
                    <div
                      data-testid={`demo-converted-badge-${order.order_id}`}
                      style={{
                        background: '#DCFCE7', border: '1px solid #86EFAC',
                        padding: 10, fontSize: 13, color: '#166534',
                      }}
                    >
                      <strong>Converted to paid order:</strong>{' '}
                      <code style={{ fontSize: 12 }}>
                        {order.demo_metadata.converted_to_order_id}
                      </code>
                      {order.demo_metadata.converted_at && (
                        <span style={{ color: '#4B5563', marginLeft: 8 }}>
                          · {order.demo_metadata.converted_at.slice(0, 10)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <button
                      data-testid={`convert-demo-btn-${order.order_id}`}
                      onClick={(e) => { e.stopPropagation(); onConvertClick(order); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '8px 16px', fontSize: 13, fontWeight: 700,
                        background: '#002FA7', color: '#fff', border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <Repeat size={14} />
                      Create Paid Order from Demo
                    </button>
                  )}
                </div>
              )}
              <AdvancedAuditDetails order={order} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const chipStyle = (active) => ({
  display: 'inline-block', padding: '4px 10px', marginRight: 6, marginBottom: 6,
  background: active ? '#002FA7' : '#F3F4F6',
  color: active ? '#fff' : '#374151',
  border: `1px solid ${active ? '#002FA7' : '#E5E7EB'}`,
  borderRadius: 3, fontSize: 11, fontWeight: 700,
  cursor: 'pointer', letterSpacing: 0.4, textTransform: 'uppercase',
});

const UsbHistoryTable = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');  // all | customer | demo | replacement | internal | legacy
  const [bandFilter, setBandFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(`${API}/api/usb-orders?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data?.orders || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (e.message || 'Could not load history'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      // Type filter
      const ft = o.fulfillment_type || null;
      if (typeFilter !== 'all') {
        if (typeFilter === 'customer' && ft !== 'customer') return false;
        if (typeFilter === 'demo' && ft !== 'demo') return false;
        if (typeFilter === 'replacement' && ft !== 'replacement') return false;
        if (typeFilter === 'internal' && ft !== 'internal') return false;
        if (typeFilter === 'legacy' && ft) return false; // legacy = no fulfillment_type at all
      }
      if (bandFilter !== 'all' && o.initial_grade_band !== bandFilter) return false;
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        o.school_name, o.district_name, o.order_id,
        o?.buyer_contact?.name, o?.buyer_contact?.email,
        ...(o.drives || []).map((d) => d.serial),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, search, typeFilter, bandFilter, statusFilter]);

  return (
    <div data-testid="usb-history-table">
      <div style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative', maxWidth: 500, marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', top: 11, left: 10, color: '#9CA3AF' }} />
          <input
            data-testid="history-search"
            placeholder="Search by school, contact, or serial"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px 8px 30px', fontSize: 13,
              border: '1px solid #D1D5DB', background: '#fff',
            }}
          />
        </div>
        <div data-testid="history-type-chips">
          {['all', 'customer', 'demo', 'replacement', 'internal', 'legacy'].map((t) => (
            <span key={t} onClick={() => setTypeFilter(t)}
                  data-testid={`history-type-${t}`}
                  style={chipStyle(typeFilter === t)}>
              {t === 'all' ? 'All' : t === 'legacy' ? 'Legacy / Unclassified' : t[0].toUpperCase() + t.slice(1)}
            </span>
          ))}
        </div>
        <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
          <Filter size={12} style={{ color: '#9CA3AF' }} />
          <select data-testid="history-band-filter" value={bandFilter} onChange={(e) => setBandFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #D1D5DB' }}>
            <option value="all">All grade bands</option>
            {/* BLOCKER-A fix (2026-02-19): seven-band commercial SKUs appended for history filtering. */}
            {['K','1','2','3-4','5-6','7-8','9-12','K-2','K-5','3-5','6-8','K-8','K-12'].map((b) => <option key={b}>{b}</option>)}
          </select>
          <select data-testid="history-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #D1D5DB' }}>
            <option value="all">All statuses</option>
            {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>}
      {err && <div style={{ padding: 20, color: '#991B1B' }}>Error: {err}</div>}
      {!loading && !err && (
        <>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
            {filtered.length} of {orders.length} records
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #E5E7EB' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                {['Date','Serial / ID','Type','School / Recipient','Band','Status','Rep','Price'].map((h) => (
                  <th key={h} style={{ padding: '8px', fontSize: 10, fontWeight: 700, color: '#6B7280', textAlign: h === 'Price' ? 'right' : 'left', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <Row key={o.order_id} order={o}
                     isOpen={openId === o.order_id}
                     onOpen={(id) => setOpenId((cur) => cur === id ? null : id)}
                     onConvertClick={setConvertTarget} />
              ))}
            </tbody>
          </table>
        </>
      )}

      {convertTarget && (
        <ConvertDemoModal
          demo={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={() => { setConvertTarget(null); load(); }}
        />
      )}
    </div>
  );
};

export default UsbHistoryTable;
